// The breadcrumb trail for a URL: the page's own name, or for a page under
// one, the sidebar's name for its row (a hub's, like "Automations") and then
// the agent, blueprint, workflow, run or task the page is about. A tab is
// never a crumb (the tab strip says which one is open). The top bar
// (components/layout/Breadcrumbs) shows every crumb but the last: the page's
// own h1 names the last. On a hub's own pages it shows the hub's tabs
// instead (navModel's hubTabsAt). Pure, so the navigation self-test can
// check it.

import { humanizeSlug } from "@/lib/format";

import { NAV_PAGES, matchesPath, navItemFor, navPageFor } from "./navModel";

export interface Crumb {
  label: string;
  to?: string;
}

/** Real names the header already read (a workflow's, a data source's); a slug humanised without them. */
export interface CrumbNames {
  workflow?: (slug: string) => string | undefined;
  source?: (name: string) => string | undefined;
  /** A connection's name, by its id or any of its names. */
  connection?: (ref: string) => string | undefined;
  /** A run's workflow name; a task's title; a deployment's title, for the trail's last crumb (and the tab's title). */
  run?: (runId: string) => string | undefined;
  task?: (taskId: string) => string | undefined;
  deployment?: (name: string) => string | undefined;
}

/** A page's own crumb: its name and where it opens. */
function own(key: string): Crumb {
  const page = NAV_PAGES.find((p) => p.key === key);
  return page ? { label: page.label, to: page.to } : { label: humanizeSlug(key) };
}

export function crumbsFor(pathname: string, nameOf: (template: string) => string, names: CrumbNames = {}): Crumb[] {
  // /record is recording a meeting: Meetings › Record. It is not a nav
  // row (the sidebar's Record button is its current-page marker), so it
  // is resolved before the nav lookup.
  if (matchesPath(pathname, "/record")) return [own("meetings"), { label: "Record" }];

  const item = navItemFor(pathname);
  const page = navPageFor(pathname);
  if (!item || !page) return pathname === "/ui-guide" ? [{ label: "UI guide" }] : [];
  const root = own(page.key);

  if (page.key === "agents") {
    const [, , template] = pathname.split("/");
    if (!matchesPath(pathname, "/agents") || !template) return [root];
    return [root, { label: nameOf(safeDecode(template)), to: `/agents/${template}` }];
  }

  // /subagents/<template>[/<view>]: a blueprint's page; its tab or Settings
  // section is the page's own tab strip, not a crumb.
  if (page.key === "subagents") {
    const [, , template] = pathname.split("/");
    if (!matchesPath(pathname, "/subagents") || !template) return [root];
    if (template === "new") return [root, { label: "New subagent" }];
    return [root, { label: nameOf(safeDecode(template)), to: `/subagents/${template}` }];
  }

  // Under /workflows: /workflows/new and /workflows/<slug> (the editor, by
  // the workflow's name once the list is read, the slug humanised before)
  // under Workflows; /workflows/runs/<id> (a run, by its workflow's name once
  // read; /report is the same run on its Result view), /workflows/jobs/new and
  // /workflows/jobs/<id> (a Team task) under Runs. No hub crumb on these: it
  // opens the same page as Workflows. Below the hub's name ("Automations",
  // opening the Workflows page): /workflows/data[/<name>] (the data sources, a source);
  // /workflows/saved-results[/<name>] (the saved results, one by its name),
  // /workflows/published (every key and what it can use from outside),
  // /workflows/deployments[/new|/<name>] (the deployments, Deploy, one by name).
  // /workflows itself is the Workflows page (its Runs view is ?tab=activity).
  if (matchesPath(pathname, "/workflows")) {
    const [, , second, third] = pathname.split("/");
    if (!second) return [own("workflows")];
    const hub: Crumb = { label: item.label, to: "/workflows" };
    const runs = own("runs");
    if (second === "new") return [own("workflows"), { label: "New workflow" }];
    if (second === "runs") return third ? [runs, { label: names.run?.(safeDecode(third)) || "Run" }] : [hub, { label: runs.label }];
    if (second === "jobs") {
      if (!third) return [hub, { label: runs.label }];
      return [{ ...runs, to: `${runs.to}&kind=job` }, { label: third === "new" ? "New Team task" : names.run?.(safeDecode(third)) || "Team task" }];
    }
    if (second === "data") {
      if (!third) return [hub, { label: "Data sources" }];
      const name = safeDecode(third);
      return [hub, { label: "Data sources", to: "/workflows/data" }, { label: names.source?.(name) || humanizeSlug(name) }];
    }
    if (second === "saved-results") {
      if (!third) return [hub, { label: "Saved results" }];
      return [hub, { label: "Saved results", to: "/workflows/saved-results" }, { label: safeDecode(third) }];
    }
    if (second === "published") return [hub, { label: "Published" }];
    if (second === "deployments") {
      if (!third) return [hub, { label: "Deployments" }];
      const list = { label: "Deployments", to: "/workflows/deployments" };
      return [hub, list, { label: third === "new" ? "Deploy" : names.deployment?.(safeDecode(third)) || safeDecode(third) }];
    }
    const slug = safeDecode(second);
    return [own("workflows"), { label: names.workflow?.(slug) || humanizeSlug(slug) }];
  }

  // /tasks/<id>: one task, under Tasks (the hub's name and this page's).
  if (page.key === "tasks") {
    const taskId = pathname.split("/")[2];
    return taskId ? [{ label: item.label, to: "/tasks" }, { label: names.task?.(safeDecode(taskId)) || "Task" }] : [root];
  }

  // /code/apps/<slug>: one app's own page (apps spec §10.3), under Code › Apps.
  if (page.key === "code") {
    const [, , second, third] = pathname.split("/");
    if (second === "apps" && third) return [root, { label: "Apps", to: "/code/apps" }, { label: humanizeSlug(safeDecode(third)) }];
  }

  // /settings/connections/<id>/explore: a connection's Explore, under Settings › Connections
  // (Settings' sections are its hash, not crumbs; this one is a page of its own).
  if (page.key === "settings") {
    const [, , second, third, fourth] = pathname.split("/");
    if (second === "connections" && third && fourth === "explore") {
      const ref = safeDecode(third);
      return [root, { label: "Connections", to: "/settings#connections" }, { label: names.connection?.(ref) || humanizeSlug(ref) }];
    }
  }

  // Code's sections are tabs: Code alone. Every other page: its own name.
  return [root];
}

// A mangled link (/agents/%E0%A4/chat) must not throw while the header
// renders: show the segment as typed instead.
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** What a page calls itself in the tab: the trail's last crumb, or the tab or section the address picks. "" = none. */
export function pageNameFor(
  location: { pathname: string; search: string; hash: string },
  nameOf: (template: string) => string,
  names: CrumbNames & { project?: (id: string) => string | undefined; settingsSection?: (key: string) => string | undefined } = {},
): string {
  const { pathname, search, hash } = location;
  const params = new URLSearchParams(search);
  if (pathname === "/workflows" && params.get("tab") === "activity") return "Runs";
  if (pathname === "/subagents" && params.get("open")) return nameOf(params.get("open") ?? "");
  if (pathname === "/projects") return names.project?.(params.get("project") ?? "") || "Projects";
  if (pathname === "/settings") return names.settingsSection?.(hash.replace(/^#/, "")) || "Settings";
  return crumbsFor(pathname, nameOf, names).at(-1)?.label ?? "";
}
